import _                    from 'underscore';
import React, { ReactNode } from 'react';
import { ApolloError }      from 'apollo-client';
import { useApolloClient }  from "@apollo/react-hooks";

import { Uuid }                  from "../types";
import { DecisionEngineQueries } from "../queries";


type DecisionEngineTypes = 'activity' | 'objective' | 'subobjective' | 'question' | 'data-value-set' | 'category' | 'subcategory' | 'assumption-group' | 'hire-type' | 'professional-fee' | 'business-function' | 'milestone';

type UseDecisionEngineHookMethod = () => UseDecisionEngineHook;

export type UseDecisionEngineHook = {
    graphQLErrorIncludesRefCodeUniquenessIssue: ( e: ApolloError ) => boolean,
    getReferentialCodeErrorMessage: ( type: DecisionEngineTypes, refIdCode: string | number ) => ( { title: string, message: ReactNode } )
    getReferentialCodePrefixForType: ( type: DecisionEngineTypes ) => string,
    getNextCategoryOrdinal: () => Promise<number>;
    getNextIntakeQuestionOrdinal: () => Promise<number>;
    getNextDataValueSetOrdinal: () => Promise<number>;
    getNextSubCategoryOrdinal: ( categoryId: Uuid ) => Promise<number>;
    getNextObjectiveOrdinal: () => Promise<number>;
    getNextSubObjectiveOrdinal: ( objectiveId: Uuid ) => Promise<number>;
};

export const useDecisionEngine: UseDecisionEngineHookMethod = () => {

    const apolloClient = useApolloClient();

    const graphQLErrorIncludesRefCodeUniquenessIssue: UseDecisionEngineHook['graphQLErrorIncludesRefCodeUniquenessIssue'] = ( e ) => {

        const specificErrMessages = e.graphQLErrors;

        if( specificErrMessages ){

            const hasRefIdCodeErr = _.filter( e.graphQLErrors, ( err: any ) => {
                return (
                    err.message?.search( 'RefIdCode' ) ||
                    err.extensions?.message?.search( 'RefIdCode' )
                );
            });

            // Reference Code issue?
            if( hasRefIdCodeErr.length ){

                const refIdErr        = _.first( hasRefIdCodeErr ),
                      refIdErrMessage = refIdErr.extensions.message,
                      uniquenessErr   = refIdErrMessage.search( 'unique' );

                if( uniquenessErr ){

                    return true;
                }

            }
        }

        return false;
    };

    const getReferentialCodeErrorMessage: UseDecisionEngineHook['getReferentialCodeErrorMessage'] = ( type, refIdCode ) => {

        const typeMap = new Map<DecisionEngineTypes | 'generic', { prefix: string, entity: string }>( [
            [ 'activity', { prefix: getReferentialCodePrefixForType( 'activity' ), entity: 'activity' } ],
            [ 'objective', { prefix: getReferentialCodePrefixForType( 'objective' ), entity: 'objective' } ],
            [ 'subobjective', { prefix: getReferentialCodePrefixForType( 'subobjective' ), entity: 'subobjective' } ],
            [ 'question', { prefix: getReferentialCodePrefixForType( 'question' ), entity: 'question' } ],
            [ 'data-value-set', { prefix: getReferentialCodePrefixForType( 'data-value-set' ), entity: 'data-value-set' } ],
            [ 'category', { prefix: getReferentialCodePrefixForType( 'category' ), entity: 'category' } ],
            [ 'subcategory', { prefix: getReferentialCodePrefixForType( 'category' ), entity: 'category' } ],
            [ 'hire-type', { prefix: getReferentialCodePrefixForType( 'hire-type' ), entity: 'hire-type' } ],
            [ 'professional-fee', { prefix: getReferentialCodePrefixForType( 'professional-fee' ), entity: 'professional-fee' } ],
            [ 'business-function', { prefix: getReferentialCodePrefixForType( 'business-function' ), entity: 'business-function' } ],
            [ 'milestone', { prefix: getReferentialCodePrefixForType( 'milestone' ), entity: 'milestone' } ],
            [ 'generic', { prefix: getReferentialCodePrefixForType( null ), entity: 'entity' } ],
        ] );

        const typeParts = typeMap.get( type ) ?? typeMap.get( 'generic' );

        return {
            title:   `Reference Code Issue (${ typeParts.prefix }-${ refIdCode })`,
            message: <>
                         <p>Corval could not update this { typeParts.entity } because you are trying to use a reference code that is already assigned to another { typeParts.entity } within this tenant.</p>
                         <p>Refer to Corval's master reference code listing and choose a different code to use for this { typeParts.entity }.</p>
                     </>
        };
    };

    const getReferentialCodePrefixForType: UseDecisionEngineHook['getReferentialCodePrefixForType'] = ( type ) => {

        const typeMap = new Map<DecisionEngineTypes | 'generic', string>( [
            [ 'activity', 'ACT' ],
            [ 'objective', 'OBJ' ],
            [ 'subobjective', 'SUBOBJ' ],
            [ 'question', 'IQ' ],
            [ 'data-value-set', 'DVSET' ],
            [ 'category', 'CAT' ],
            [ 'subcategory', 'SUBCAT' ],
            [ 'hire-type', 'HIRETYPE' ],
            [ 'professional-fee', 'PROFEE' ],
            [ 'business-function', 'BUSFUNC' ],
            [ 'milestone', 'MILESTONE' ],
            [ 'generic', 'ENTITY' ],
        ] );

        return typeMap.get( type ) ?? typeMap.get( 'generic' );
    };

    const getNextCategoryOrdinal: UseDecisionEngineHook['getNextCategoryOrdinal'] = async () => {

        return await apolloClient
            .query( {
                query: DecisionEngineQueries.queries.GET_CATEGORIES( `id ordinal` ),
            } )
            .then( ( { data } ) => {
                const ordinalMax = data.categories.length;
                return Promise.resolve( ordinalMax );
            } )
            .catch( e => {
                throw e;
            } );
    };

    const getNextIntakeQuestionOrdinal: UseDecisionEngineHook['getNextCategoryOrdinal'] = async () => {

        return await apolloClient
            .query( {
                query: DecisionEngineQueries.queries.GET_INTAKE_QUESTIONS( `id ordinal` ),
            } )
            .then( ( { data } ) => {
                const ordinalMax = data.intakeQuestions.length;
                return Promise.resolve( ordinalMax );
            } )
            .catch( e => {
                throw e;
            } );
    };


    const getNextDataValueSetOrdinal: UseDecisionEngineHook['getNextDataValueSetOrdinal'] = async () => {

        return await apolloClient
            .query( {
                query: DecisionEngineQueries.queries.GET_DATA_VALUE_SETS( `id ordinal` ),
            } )
            .then( ( { data } ) => {
                const ordinalMax = data.dataValueSets.length;
                return Promise.resolve( ordinalMax );
            } )
            .catch( e => {
                throw e;
            } );
    };

    const getNextSubCategoryOrdinal: UseDecisionEngineHook['getNextSubCategoryOrdinal'] = async ( categoryId ) => {

        if( !categoryId ) return Promise.resolve( 0 );

        return await apolloClient
            .query( {
                query:     DecisionEngineQueries.queries.GET_SUBCATEGORIES( `id ordinal` ),
                variables: {
                    where: {
                        categoryId: { eq: categoryId },
                    }
                }
            } )
            .then( ( { data } ) => {
                const ordinalMax = data.subCategories.length;
                return Promise.resolve( ordinalMax );
            } )
            .catch( e => {
                throw e;
            } );
    };

    const getNextObjectiveOrdinal: UseDecisionEngineHook['getNextObjectiveOrdinal'] = async () => {

        return await apolloClient
            .query( {
                query: DecisionEngineQueries.queries.GET_OBJECTIVES( `id ordinal` ),
            } )
            .then( ( { data } ) => {
                const ordinalMax = data.objectives.length;
                return Promise.resolve( ordinalMax );
            } )
            .catch( e => {
                throw e;
            } );
    };

    const getNextSubObjectiveOrdinal: UseDecisionEngineHook['getNextSubObjectiveOrdinal'] = async ( objectiveId ) => {

        if( !objectiveId ) return Promise.resolve( 0 );

        return await apolloClient
            .query( {
                query:     DecisionEngineQueries.queries.GET_SUBOBJECTIVES( `id ordinal` ),
                variables: {
                    where: {
                        objectiveId: { eq: objectiveId },
                    }
                }
            } )
            .then( ( { data } ) => {
                const ordinalMax = data.subObjectives.length;
                return Promise.resolve( ordinalMax );
            } )
            .catch( e => {
                throw e;
            } );
    };

    return {
        graphQLErrorIncludesRefCodeUniquenessIssue,
        getReferentialCodeErrorMessage,
        getReferentialCodePrefixForType,
        getNextIntakeQuestionOrdinal,
        getNextCategoryOrdinal,
        getNextDataValueSetOrdinal,
        getNextSubCategoryOrdinal,
        getNextObjectiveOrdinal,
        getNextSubObjectiveOrdinal,
    };
};
